import React, { useState, useEffect } from 'react';
import { FaTrophy } from 'react-icons/fa';
import { getLastResults, getDetailResults } from '../../../../apiservices/CasionApi';
import ResultModalLayout from '../../../../components/resultModalLayout';
import "./Result.css";

// Helper function to map card string to image path
const getCardImage = (cardString) => {
  if (!cardString) return null;
  // Use local asset paths - try tablecard first, then card-results as fallback
  return `/assets/img/tablecard/${cardString}.jpg`;
};

// Helper function to parse card string for teensin (format: "3HH,6HH,3DD,2CC,7SS,7DD" = 3 cards for Player A, 3 cards for Player B)
const parseCards = (cardString) => {
  if (!cardString) return { playerA: [], playerB: [] };
  
  const cards = cardString.split(',');
  const playerACards = cards.slice(0, 3).map(card => getCardImage(card.trim()));
  const playerBCards = cards.slice(3, 6).map(card => getCardImage(card.trim()));
  
  return { playerA: playerACards, playerB: playerBCards };
};

// Helper function to parse rdesc for teensin
// Format: "Player B  (A: 3 | B: 5)#Player B#Player A#-#No"
// Parts: [0] Winner with scores, [1] High Card, [2] Pair, [3] Color Plus, [4] Lucky 9
const parseRdesc = (rdesc) => {
  if (!rdesc) return {};
  
  const parts = rdesc.split('#').map(p => p.trim());
  
  return {
    winner: parts[0] || "",      // "Player B  (A: 3 | B: 5)"
    highCard: parts[1] || "",    // "Player B"
    pair: parts[2] || "",        // "Player A"
    colorPlus: parts[3] || "",   // "-"
    lucky9: parts[4] || "",      // "No"
  };
};

const ResultTeensin = ({ 
  data = [], 
  gameData = {},
}) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [lastResults, setLastResults] = useState([]);
  const [detailResult, setDetailResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch last results on mount
  useEffect(() => {
    const fetchLastResults = async () => {
      try {
        const response = await getLastResults('teensin');
        if (response?.data?.data?.res) {
          // Map win values: "1" = "A" (Player A), "2" = "B" (Player B)
          const mappedResults = response.data.data.res.map(item => ({
            mid: item.mid,
            win: item.win === "1" ? "A" : item.win === "2" ? "B" : item.win
          }));
          setLastResults(mappedResults);
        }
      } catch (error) {
        console.error('Error fetching last results:', error);
      }
    };
    
    fetchLastResults();
  }, []);

  const handleResultClick = async (result, index) => {
    setSelectedResult(result);
    setLoading(true);
    setShowModal(true);
    
    // First, ensure we have last results
    if (lastResults.length === 0) {
      try {
        const response = await getLastResults('teensin');
        if (response?.data?.data?.res) {
          const mappedResults = response.data.data.res.map(item => ({
            mid: item.mid,
            win: item.win === "1" ? "A" : item.win === "2" ? "B" : item.win
          }));
          setLastResults(mappedResults);
        }
      } catch (error) {
        console.error('Error fetching last results:', error);
      }
    }
    
    // Find the mid for this result
    let mid = null;
    if (index < lastResults.length) {
      mid = lastResults[index]?.mid;
    } else if (index < data.length + (gameData?.lrs?.length || 0)) {
      mid = gameData?.data?.data?.mid || gameData?.mid;
    }
    
    // Fetch detail results if we have a mid
    if (mid) {
      try {
        const response = await getDetailResults('teensin', mid.toString());
        if (response?.data?.data?.t1) {
          setDetailResult(response.data.data.t1);
        }
      } catch (error) {
        console.error('Error fetching detail results:', error);
      }
    }
    
    setLoading(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedResult(null);
    setDetailResult(null);
  };

  // Format modal data based on selected result
  const getModalData = () => {
    if (!selectedResult && !detailResult) return null;

    // Use detailResult if available, otherwise fallback to defaults
    const cards = detailResult?.card ? parseCards(detailResult.card) : { 
      playerA: [], 
      playerB: [] 
    };
    
    const rdescData = detailResult?.rdesc ? parseRdesc(detailResult.rdesc) : {};
    const winner = detailResult?.winnat || (selectedResult === "A" ? "Player A" : selectedResult === "B" ? "Player B" : "");
    
    // Determine winner from API data - check win field first, then winnat, then selectedResult
    const winValue = detailResult?.win;
    const isPlayerAWinner = winValue === "1" || winner === "Player A" || selectedResult === "A";
    const isPlayerBWinner = winValue === "2" || winner === "Player B" || selectedResult === "B";

    return {
      roundId: detailResult?.rid || gameData?.data?.data?.mid || gameData?.mid || "N/A",
      matchTime: detailResult?.mtime || gameData?.data?.data?.mt || new Date().toLocaleString(),
      playerA: {
        cards: cards.playerA,
        isWinner: isPlayerAWinner,
      },
      playerB: {
        cards: cards.playerB,
        isWinner: isPlayerBWinner,
      },
      resultDetails: {
        winner: rdescData.winner || winner,
        highCard: rdescData.highCard || "",
        pair: rdescData.pair || "",
        colorPlus: rdescData.colorPlus || "",
        lucky9: rdescData.lucky9 || "",
      },
    };
  };

  const modalData = getModalData();
  const {
    winner = "",
    highCard = "",
    pair = "",
    colorPlus = "",
    lucky9 = "",
  } = modalData?.resultDetails || {};

  // Combine API results with fallback data
  const allResults = lastResults.length > 0 
    ? lastResults.map(r => r.win)
    : [...data, ...(gameData?.lrs || [])];

  return (
    <>
      <div className="result-row">
        {allResults.map((res, index) => (
          <div
            key={index}
            className={`result-circle ${res === "A" ? "yes" : "no"}`}
            onClick={() => handleResultClick(res, index)}
            style={{ cursor: 'pointer' }}
          >
            {res}
          </div>
        ))}
      </div>

      {/* Modal */}
      {modalData && (
        <ResultModalLayout
          show={showModal}
          onHide={handleCloseModal}
          title="29Card Baccarat Result"
          roundId={modalData.roundId}
          matchTime={modalData.matchTime}
          loading={loading}
        >
          {/* Player A vs Player B Cards Section */}
          <div className="row mt-2">
            {/* Player A */}
            <div className="col-md-6 text-center">
              <h4 className="result-title">Player A</h4>
              <div className="casino-result-cards">
                {modalData.playerA?.isWinner && (
                  <div className="casino-winner-icon casino-winner-icon-left" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <FaTrophy color="#4caf50" size={32} />
                  </div>
                )}
                {modalData.playerA?.cards && modalData.playerA.cards.length > 0 && (
                  <div className="d-flex justify-content-center gap-2 flex-wrap">
                    {modalData.playerA.cards.map((card, idx) => (
                      card ? (
                        <img
                          key={idx}
                          src={card}
                          alt={`Player A Card ${idx + 1}`}
                          onError={(e) => {
                            // Fallback to card-results folder if tablecard doesn't exist
                            const cardName = card.split('/').pop();
                            const fallbackPath = `/assets/img/card-results/${cardName}`;
                            if (e.target.src !== fallbackPath) {
                              e.target.src = fallbackPath;
                            } else {
                              e.target.style.display = 'none';
                            }
                          }}
                          style={{ border: '2px solid #ffd700', borderRadius: '4px' }}
                        />
                      ) : null
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Player B */}
            <div className="col-md-6 text-center">
              <h4 className="result-title">Player B</h4>
              <div className="casino-result-cards">
                {modalData.playerB?.cards && modalData.playerB.cards.length > 0 && (
                  <div className="d-flex justify-content-center gap-2 flex-wrap">
                    {modalData.playerB.cards.map((card, idx) => (
                      card ? (
                        <img
                          key={idx}
                          src={card}
                          alt={`Player B Card ${idx + 1}`}
                          onError={(e) => {
                            // Fallback to card-results folder if tablecard doesn't exist
                            const cardName = card.split('/').pop();
                            const fallbackPath = `/assets/img/card-results/${cardName}`;
                            if (e.target.src !== fallbackPath) {
                              e.target.src = fallbackPath;
                            } else {
                              e.target.style.display = 'none';
                            }
                          }}
                          style={{ border: '2px solid #ffd700', borderRadius: '4px' }}
                        />
                      ) : null
                    ))}
                  </div>
                )}
                {modalData.playerB?.isWinner && (
                  <div className="casino-winner-icon casino-winner-icon-right" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '32px', minHeight: '32px' }}>
                    <FaTrophy color="#4caf50" size={32} />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Result Details */}
          <div className="row mt-2 justify-content-center">
            <div className="col-md-6">
              <div className="casino-result-desc">
                {winner && (
                  <div className="casino-result-desc-item">
                    <div>Winner:</div>
                    <div>{winner}</div>
                  </div>
                )}
                {highCard && (
                  <div className="casino-result-desc-item">
                    <div>High Card:</div>
                    <div>{highCard}</div>
                  </div>
                )}
                {pair && (
                  <div className="casino-result-desc-item">
                    <div>Pair:</div>
                    <div>{pair}</div>
                  </div>
                )}
                {colorPlus && (
                  <div className="casino-result-desc-item">
                    <div>Color Plus:</div>
                    <div>{colorPlus === "-" ? "-" : colorPlus}</div>
                  </div>
                )}
                {lucky9 && (
                  <div className="casino-result-desc-item">
                    <div>Lucky 9:</div>
                    <div>{lucky9}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </ResultModalLayout>
      )}
    </>
  );
};

export default ResultTeensin;

